import { type FC, useState, useRef, useEffect } from 'react';
import {
  parseScoringScale,
  validateScoringScale,
  formatScoringScale,
  type ScoringScale,
} from '@/utils/observationTypeValidation';
import styles from './ScoringScaleEditor.module.scss';
import { _notNil, _isNotBlank } from '@/littledash';

export interface ScoringScaleModalProps {
  defaultValue: ScoringScale | null;
  td: HTMLTableCellElement;
  onChange: (value: string) => void;
  onClose: () => void;
}

export const ScoringScaleModal: FC<ScoringScaleModalProps> = ({ defaultValue, td, onChange, onClose }) => {
  const [scaleType, setScaleType] = useState<'numeric' | 'severity' | null>(defaultValue?.type || null);
  const [minValue, setMinValue] = useState<string>(
    defaultValue?.type === 'numeric' && _notNil(defaultValue.minValue) ? String(defaultValue.minValue) : ''
  );
  const [maxValue, setMaxValue] = useState<string>(
    defaultValue?.type === 'numeric' && _notNil(defaultValue.maxValue) ? String(defaultValue.maxValue) : ''
  );
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const isActivatedRef = useRef(false);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      isActivatedRef.current = true;
    });

    const handleClickOutside = (event: MouseEvent): void => {
      if (!isActivatedRef.current) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (_notNil(modalRef.current) && !modalRef.current.contains(target) && !td.contains(target)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [td, onClose]);

  const validateMinMax = (): boolean => {
    setError(null);

    if (!_isNotBlank(minValue) || !_isNotBlank(maxValue)) {
      return true;
    }

    const min = parseFloat(minValue);
    const max = parseFloat(maxValue);

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return true;
    }

    const scale: ScoringScale = {
      type: 'numeric',
      minValue: min,
      maxValue: max,
    };

    const validation = validateScoringScale(scale);
    if (!validation.isValid) {
      setError(validation.message ?? 'Invalid scale configuration');
      return false;
    }

    return true;
  };

  const handleApply = (): void => {
    if (!_notNil(scaleType)) {
      onChange('');
      onClose();
      return;
    }

    if (scaleType === 'severity') {
      onChange('Severity');
      onClose();
      return;
    }

    const min = parseFloat(minValue);
    const max = parseFloat(maxValue);

    const scale: ScoringScale = {
      type: 'numeric',
      minValue: min,
      maxValue: max,
    };

    const validation = validateScoringScale(scale);
    if (!validation.isValid) {
      setError(validation.message ?? 'Invalid scale configuration');
      return;
    }

    onChange(formatScoringScale(scale));
    onClose();
  };

  const handleTypeChange = (type: 'numeric' | 'severity'): void => {
    setScaleType(type);
    setError(null);

    if (type === 'severity') {
      setMinValue('');
      setMaxValue('');
      onChange('Severity');
      onClose();
    }
  };

  // Position the modal relative to the cell
  const rect = td.getBoundingClientRect();
  const modalStyle = {
    position: 'fixed' as const,
    top: `${rect.bottom + 5}px`,
    left: `${rect.left}px`,
    zIndex: 9999,
  };

  return (
    <div
      ref={modalRef}
      className={styles.scoringScaleModal}
      style={modalStyle}
      data-test-component="ScoringScaleModal"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className={styles.modalContent}>
        <div className={styles.header}>Scoring Scale Configuration</div>

        <div className={styles.scaleTypeSelection}>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="scaleType"
              value="numeric"
              checked={scaleType === 'numeric'}
              onChange={() => handleTypeChange('numeric')}
              data-testid="scoring-scale-modal__numeric-radio"
            />
            <span>Numeric</span>
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              name="scaleType"
              value="severity"
              checked={scaleType === 'severity'}
              onChange={() => handleTypeChange('severity')}
              data-testid="scoring-scale-modal__severity-radio"
            />
            <span>Severity</span>
          </label>
        </div>

        {scaleType === 'numeric' && (
          <div className={styles.numericInputs}>
            <div className={styles.inputGroup}>
              <label htmlFor="minValue">Min Value</label>
              <input
                type="number"
                id="minValue"
                value={minValue}
                onChange={(e) => {
                  setMinValue(e.target.value);
                  setError(null);
                }}
                onBlur={validateMinMax}
                min="0"
                step="any"
                data-testid="scoring-scale-modal__min-value"
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="maxValue">Max Value</label>
              <input
                type="number"
                id="maxValue"
                value={maxValue}
                onChange={(e) => {
                  setMaxValue(e.target.value);
                  setError(null);
                }}
                onBlur={validateMinMax}
                min="0"
                step="any"
                data-testid="scoring-scale-modal__max-value"
              />
            </div>
          </div>
        )}

        {_notNil(error) && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          {scaleType === 'numeric' && (
            <button className={styles.applyButton} onClick={handleApply} data-testid="scoring-scale-modal__apply">
              Apply
            </button>
          )}
          <button className={styles.closeButton} onClick={onClose} data-testid="scoring-scale-modal__close">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const isValidScoringScaleValue = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') {
    return true;
  }

  if (typeof value !== 'string') {
    return false;
  }

  const trimmedValue = value.trim();

  if (trimmedValue === '') {
    return true;
  }

  const parsed = parseScoringScale(trimmedValue);
  return parsed !== null;
};
